import { useEffect, useState } from 'react';
import financasApi from '@/lib/financasApi';
import { Calculator, AlertTriangle, TrendingUp, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function FinancasMEI() {
  const [data, setData] = useState(null);

  useEffect(() => {
    financasApi.get('/financas/mei/status').then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-slate-400">Carregando...</div>;

  const corBarra = data.ultrapassou ? '#ef4444' : data.alerta ? '#f59e0b' : '#10b981';
  const chart = data.por_mes.map(m => ({ mes: meses[parseInt(m.mes.split('-')[1]) - 1], total: m.total }));
  const limiteMensal = data.limite_anual / 12;

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="financas-mei-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><Calculator className="w-6 h-6 md:w-7 md:h-7 text-emerald-400" /> Status MEI / ME</h1>
        <p className="text-slate-400 text-xs md:text-sm mt-1">Acompanhe seu faturamento e o limite anual</p>
      </div>

      {/* Barra de progresso principal */}
      <div className={`rounded-xl p-6 border mb-6 ${data.alerta ? 'bg-amber-500/5 border-amber-500/30' : 'bg-slate-900 border-white/10'}`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider">Faturamento {data.ano}</p>
            <p className="text-3xl font-bold text-white mt-1">{fmtBRL(data.faturamento_ano)}</p>
            <p className="text-sm text-slate-400 mt-1">de {fmtBRL(data.limite_anual)} permitidos</p>
          </div>
          {data.alerta && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${data.ultrapassou ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm font-semibold">{data.ultrapassou ? 'LIMITE EXCEDIDO!' : 'Próximo do limite'}</span>
            </div>
          )}
        </div>

        <div className="w-full bg-slate-800 rounded-full h-4 overflow-hidden mb-2">
          <div
            className="h-full transition-all rounded-full"
            style={{ width: `${Math.min(100, data.percentual_uso)}%`, background: corBarra }}
          />
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-slate-400">{data.percentual_uso.toFixed(1)}% utilizado</span>
          <span className="text-slate-400">{(100 - Math.min(100, data.percentual_uso)).toFixed(1)}% restante</span>
        </div>
      </div>

      {/* KPIs estimativa */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <p className="text-xs text-slate-400 uppercase tracking-wider">Média Mensal</p>
          </div>
          <p className="text-2xl font-bold text-white">{fmtBRL(data.media_mensal)}</p>
        </div>
        <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-blue-400" />
            <p className="text-xs text-slate-400 uppercase tracking-wider">Estimativa Anual</p>
          </div>
          <p className="text-2xl font-bold text-white">{fmtBRL(data.estimativa_anual)}</p>
          <p className="text-xs text-slate-500 mt-1">com base na média atual</p>
        </div>
        <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="w-4 h-4 text-purple-400" />
            <p className="text-xs text-slate-400 uppercase tracking-wider">Limite Mensal Médio</p>
          </div>
          <p className="text-2xl font-bold text-white">{fmtBRL(limiteMensal)}</p>
          <p className="text-xs text-slate-500 mt-1">para não estourar o teto</p>
        </div>
      </div>

      {/* Gráfico mensal */}
      <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-4">Faturamento por Mês ({data.ano})</h3>
        {chart.length === 0 ? (
          <div className="h-[280px] flex items-center justify-center text-slate-500">Sem dados em {data.ano}</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="mes" stroke="#64748b" />
              <YAxis stroke="#64748b" tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }} formatter={(v) => fmtBRL(v)} />
              <ReferenceLine y={limiteMensal} stroke="#ef4444" strokeDasharray="3 3" label={{ value: 'Limite mensal', fill: '#ef4444', fontSize: 11 }} />
              <Bar dataKey="total" fill="#10b981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
